import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import * as PhosphorIcons from "@phosphor-icons/react";
import {
  CopySimple,
  DotsSixVertical,
  DotsThree,
  Eye,
  EyeSlash,
  PencilSimple,
  TrashSimple,
} from "@phosphor-icons/react";
import { useTouchOptimization } from "@stylingcv/hooks";
import { cn } from "@stylingcv/utils";
import { useEffect, useRef, useState } from "react";

import { useDialogStore } from "@/client/stores/dialog";
import { isRTLLocale } from "@/client/utils/rtl-helpers";

function kebabToPascal(str: string): string {
  return str
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

function resolvePhosphorIcon(icon: string): React.ComponentType<any> | null {
  const name = icon.trim();
  if (!name) return null;
  const icons = PhosphorIcons as Record<string, any>;
  // Try exact kebab→Pascal conversion (e.g. "rocket-launch" → "RocketLaunch")
  const exact = icons[kebabToPascal(name)];
  if (exact) return exact;
  // Try the string as-is in case it's already PascalCase (e.g. "Reading")
  const asIs = icons[name];
  if (asIs) return asIs;
  // Strip trailing digits and retry (e.g. "trophy2" → "Trophy")
  const noDigits = icons[kebabToPascal(name.replace(/\d+$/, ""))];
  if (noDigits) return noDigits;
  return null;
}

export type SectionListItemProps = {
  id: string;
  title: string;
  visible?: boolean;
  description?: string;
  date?: string;
  icon?: string;
  iconFamily?: "phosphor" | "hobby" | "icomoon";
  level?: number;
  keywords?: string[];
  isDragOverlay?: boolean;

  onUpdate?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onToggleVisibility?: () => void;
};

export const SectionListItem = ({
  id,
  title,
  description,
  date,
  icon,
  iconFamily = "phosphor",
  level,
  keywords,
  visible = true,
  isDragOverlay,
  onUpdate,
  onDuplicate,
  onDelete,
  onToggleVisibility,
}: SectionListItemProps) => {
  const { i18n } = useLingui();
  const isRTL = isRTLLocale(i18n.locale);
  const ItemIcon = icon && iconFamily === "phosphor" ? resolvePhosphorIcon(icon) : null;
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id,
  });
  const { triggerHaptic } = useTouchOptimization();
  const wasDraggingRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isEditing = useDialogStore(
    (s) => s.dialog?.mode === "update" && s.dialog?.payload?.id === id,
  );

  useEffect(() => {
    if (isDragging && !wasDraggingRef.current) triggerHaptic("medium");
    else if (!isDragging && wasDraggingRef.current) triggerHaptic("success");
    wasDraggingRef.current = isDragging;
  }, [isDragging, triggerHaptic]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [menuOpen]);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={isDragOverlay ? undefined : { ...style, ...(menuOpen ? { position: "relative", zIndex: 10 } : {}) }}
      className={cn("mb-2", isDragging && !isDragOverlay && "opacity-30")}
    >
      <div
        className={cn(
          // Base (narrow < 280px): compact padding, tight gap
          "group flex cursor-pointer items-center rounded-[12px] border bg-white transition-all duration-150",
          "gap-2 p-3",
          // Medium ≥ 280px: more breathing room
          "@[280px]:gap-3 @[280px]:px-4",
          // Wide ≥ 360px: original full spacing
          "@[360px]:gap-[14px] @[360px]:px-[18px] @[360px]:py-[14px]",
          !visible && "opacity-65",
          isEditing
            ? "border-[var(--scv-coral,#e8885e)] shadow-[0_0_0_3px_rgba(232,136,94,0.10)]"
            : "border-[var(--border,#e5e5e5)] hover:-translate-y-px hover:border-[var(--accent,#268599)] hover:shadow-[0_2px_8px_rgba(22,52,76,0.08)]",
          isDragOverlay &&
            "ring-[var(--accent,#268599)]/40 shadow-[var(--shadow-md,0_4px_20px_rgba(22,52,76,0.10))] ring-1",
        )}
        onClick={onUpdate}
      >
        {/* Drag handle — always visible at all container sizes */}
        <div
          {...listeners}
          {...attributes}
          role="button"
          aria-label={t`Drag to reorder`}
          className="flex shrink-0 cursor-grab touch-manipulation active:cursor-grabbing"
          style={{ color: "var(--fg-4, #a3a3a3)" }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DotsSixVertical size={12} weight="bold" aria-hidden="true" />
        </div>

        {/* Status dot */}
        <div
          className="size-2 shrink-0 rounded-full transition-colors duration-150"
          style={{ background: visible ? "var(--scv-success, #22c55e)" : "#d4d4d4" }}
        />

        {/* Per-item icon */}
        {icon && (
          <span className="shrink-0" style={{ color: "var(--accent,#268599)" }}>
            {iconFamily === "hobby" ? (
              <i
                className={`stylingcvHobbies iconHobbies ${icon}`}
                style={{ fontSize: 18 }}
                aria-hidden="true"
              />
            ) : iconFamily === "icomoon" ? (
              <i className={`fa-${icon}`} style={{ fontSize: 18 }} aria-hidden="true" />
            ) : ItemIcon ? (
              <ItemIcon size={15} weight="duotone" />
            ) : null}
          </span>
        )}

        {/* Meta — flex-1, contains title + description + date-inline-when-narrow */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 leading-tight">
            <span
              className="truncate font-bold"
              style={{ fontSize: "clamp(12px, 3.5cqw, 14px)", color: "var(--fg-1, #171717)" }}
            >
              {title}
            </span>
            {isEditing && (
              <span
                className="hidden @[280px]:inline-flex"
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--scv-coral-deep, #cd7858)",
                  background: "rgba(232,136,94,0.12)",
                  padding: "2px 7px",
                  borderRadius: 9999,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {t`Editing`}
              </span>
            )}
          </div>

          {description && (
            <p
              className="mt-[3px] line-clamp-1 text-[11px] @[280px]:text-[12px]"
              style={{ color: "var(--fg-3, #737373)" }}
            >
              {description}
            </p>
          )}

          {/* Date shown inline in meta when container is too narrow for the badge */}
          {date && (
            <p
              className="mt-[3px] line-clamp-1 text-[10px] font-medium @[360px]:hidden"
              style={{ color: "var(--fg-4, #a3a3a3)" }}
            >
              {date}
            </p>
          )}

          {keywords && keywords.length > 0 && (
            <div className="mt-[4px] flex flex-wrap gap-1">
              {keywords.map((kw, i) => (
                <span
                  key={i}
                  className="rounded px-[5px] py-[2px] text-[9px] font-semibold @[280px]:text-[10px]"
                  style={{ background: "rgba(38,133,153,0.10)", color: "var(--accent,#268599)" }}
                >
                  {kw}
                </span>
              ))}
            </div>
          )}

          {level !== undefined && level > 0 && (
            <div className="mt-[6px] flex items-center gap-2">
              <div className="h-[3px] flex-1 rounded-full" style={{ background: "#ebebeb" }}>
                <div
                  className="h-[3px] rounded-full transition-all duration-300"
                  style={{ width: `${(level / 5) * 100}%`, background: "var(--accent,#268599)" }}
                />
              </div>
              <span
                className="shrink-0 text-[10px] font-semibold"
                style={{ color: "var(--fg-3,#737373)" }}
              >
                {level}/5
              </span>
            </div>
          )}
        </div>

        {/* Date badge — only on wide containers (≥ 360px) */}
        {date && (
          <span
            className="hidden shrink-0 whitespace-nowrap rounded-full px-[10px] py-[5px] text-[11px] font-semibold @[360px]:inline-flex"
            style={{ background: "#F5F5F5", color: "var(--fg-3, #737373)" }}
          >
            {date}
          </span>
        )}

        {/* Right action zone: always-visible ⋯ button */}
        <div
          ref={menuRef}
          className="relative flex size-6 shrink-0 items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <button
            className={cn(
              "flex size-6 items-center justify-center rounded-[6px] transition-colors duration-150",
              menuOpen ? "bg-gray-100" : "hover:bg-gray-100",
            )}
            style={{ color: "var(--fg-3, #737373)" }}
            aria-label={t`Item options`}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <DotsThree size={15} weight="bold" />
          </button>

          {/* Custom dropdown panel */}
          {menuOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                ...(isRTL ? { left: 0 } : { right: 0 }),
                zIndex: 200,
                background: "#fff",
                color: "var(--fg-1, #171717)",
                borderRadius: 10,
                boxShadow: "0 8px 28px rgba(22,52,76,0.12)",
                border: "1px solid rgba(0,0,0,0.06)",
                padding: 4,
                width: 160,
                minWidth: "max-content",
              }}
            >
              <ItemMenuBtn
                icon={<PencilSimple size={13} />}
                label={t`Edit`}
                onClick={() => {
                  setMenuOpen(false);
                  onUpdate?.();
                }}
              />
              <ItemMenuBtn
                icon={visible ? <EyeSlash size={13} /> : <Eye size={13} />}
                label={visible ? t`Hide` : t`Show`}
                onClick={() => {
                  setMenuOpen(false);
                  onToggleVisibility?.();
                }}
              />
              <ItemMenuBtn
                icon={<CopySimple size={13} />}
                label={t`Duplicate`}
                onClick={() => {
                  setMenuOpen(false);
                  onDuplicate?.();
                }}
              />
              <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
              <ItemMenuBtn
                danger
                icon={<TrashSimple size={13} />}
                label={t`Delete`}
                onClick={() => {
                  setMenuOpen(false);
                  onDelete?.();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tiny helper — one row in the item dropdown (always white, immune to dark mode)
// ---------------------------------------------------------------------------
type ItemMenuBtnProps = {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
};

const ItemMenuBtn = ({ icon, label, danger, onClick }: ItemMenuBtnProps) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 6,
        border: "none",
        background: hovered ? (danger ? "rgba(220,38,38,0.05)" : "#f5f5f5") : "transparent",
        color: danger ? (hovered ? "#dc2626" : "var(--fg-3, #737373)") : "var(--fg-1, #171717)",
        cursor: "pointer",
        transition: "background 0.1s, color 0.1s",
      }}
      onClick={onClick}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      <span
        style={{ flexShrink: 0, color: danger && hovered ? "#dc2626" : "var(--fg-3, #737373)" }}
      >
        {icon}
      </span>
      {label}
    </button>
  );
};
